import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Globe2, Search } from 'lucide-react';
import FeaturedLayout from '../layouts/FeaturedLayout';
import { Badge, Button, Card, CardContent, Input } from '../ui';
import { useAuth } from '../../hooks/useAuth';
import { useOrganization } from '../../contexts/OrganizationContext';
import { usePermissions } from '../../hooks/usePermissions';
import { filtrarPorMundo, MUNDO_RED } from '../../lib/mundoTorneo';
import { getAllGroups, getJuegosByGrupoIds, getStaffGroups } from '../../lib/supabase';
import { agruparJuegosPorFecha } from '../../lib/juegosPorFecha';
import JuegoCard from './JuegoCard';

// Los juegos que la organizadora programa en los torneos de la red donde el club
// participa. Es la pareja de "Juegos" de Torneos internos, en su propia casa: misma
// forma (lista por fecha, busqueda), pero de SOLO LECTURA -- aqui el club no crea ni
// edita nada; ve, toma asistencia e imprime tarjeton. Antes estos juegos solo se
// alcanzaban torneo por torneo, en la pestana Rol de cada participacion (el dueno,
// 2026-09-07, al separar los mundos en Juegos).
const JuegosDeLaRed = () => {
  const { user, staff } = useAuth();
  const { org } = useOrganization();
  const { isAdmin, isHeadCoach, canTakeAttendance } = usePermissions();
  const navigate = useNavigate();

  const [juegos, setJuegos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Por id, no por objeto: un objeto de usuario nuevo de la misma persona (al volver del
  // segundo plano) no debe volver a cargar. Misma leccion que OrganizationContext.
  const userId = user?.id;
  const loadData = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      // Mismo alcance que Juegos: admin y head coach ven todos los grupos; el resto,
      // los suyos.
      let groupData = [];
      if (isAdmin || isHeadCoach) {
        const { data } = await getAllGroups();
        groupData = data || [];
      } else if (staff?.id) {
        const { data } = await getStaffGroups(staff.id);
        groupData = (data || []).map((item) => item.grupos).filter(Boolean);
      }
      const todos = await getJuegosByGrupoIds(groupData.map((g) => g.id));
      setJuegos(todos || []);
    } catch (error) {
      console.error('Error cargando juegos de la red:', error);
      setJuegos([]);
    } finally {
      setLoading(false);
    }
  }, [userId, staff?.id, isAdmin, isHeadCoach]);

  useEffect(() => { loadData(); }, [loadData]);

  const juegosRed = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return filtrarPorMundo(juegos, MUNDO_RED, org?.id).filter((juego) => {
      if (!term) return true;
      return (
        juego.nombre?.toLowerCase().includes(term) ||
        juego.equipo?.nombre?.toLowerCase().includes(term) ||
        juego.torneo?.nombre?.toLowerCase().includes(term) ||
        juego.grupo?.nombre_del_grupo?.toLowerCase().includes(term)
      );
    });
  }, [juegos, searchTerm, org?.id]);

  const porFecha = useMemo(() => agruparJuegosPorFecha(juegosRed), [juegosRed]);

  return (
    <FeaturedLayout
      title="Juegos"
      subtitle="Los partidos que programa la organizadora en los torneos donde participa el club. Aquí solo ves y tomas asistencia."
      refreshAction={loadData}
    >
      <div className="space-y-4">
        <div className="flex items-center gap-2 flex-nowrap">
          <div className="flex-1 min-w-0 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input placeholder="Buscar por rival, torneo o grupo..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
          </div>
          <span className="text-sm text-muted-foreground hidden sm:block whitespace-nowrap">
            <span className="font-medium text-foreground">{juegosRed.length}</span> juegos
          </span>
        </div>

        {loading ? (
          <p className="text-muted-foreground text-sm py-8 text-center">Cargando juegos...</p>
        ) : juegosRed.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Globe2 className="h-12 w-12 mx-auto mb-3 text-blue-700 opacity-40" />
              <p className="text-muted-foreground mb-1">
                {juegos.length === 0 || !searchTerm
                  ? 'Todavía no hay partidos de la red para tus grupos.'
                  : 'No se encontraron juegos con esa búsqueda.'}
              </p>
              <p className="text-xs text-muted-foreground">
                Aparecen solos cuando la organizadora publica el rol de un torneo donde el club está inscrito.
              </p>
              <Button size="sm" variant="outline" className="mt-4" onClick={() => navigate('/torneos-myklubb')}>
                Ir a Mis torneos
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {porFecha.map(({ fecha, label, juegos: items }) => (
              <div key={fecha}>
                <div className="flex items-center gap-2 mb-3">
                  <h3 className="text-base font-semibold capitalize">{label}</h3>
                  <Badge variant="secondary" className="text-xs">{items.length}</Badge>
                </div>
                <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                  {items.map((juego) => (
                    <JuegoCard
                      key={juego.id}
                      juego={juego}
                      orgId={org?.id}
                      compact
                      onView={() => navigate(`/torneos/juegos/${juego.id}`)}
                      onTakeAttendance={canTakeAttendance ? () => {
                        const gId = juego.grupo_id || juego.grupos?.id;
                        if (gId) navigate(`/attendance/juego/${gId}/${juego.id}`);
                      } : null}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </FeaturedLayout>
  );
};

export default JuegosDeLaRed;
